import React, { Children } from 'react';
import { motion } from 'framer-motion';
import { PageHero } from '../components/PageHero';
import { AwardIcon, HeartHandshakeIcon, UsersIcon, ShieldCheckIcon } from 'lucide-react';
const providers = [{
  name: 'Dr. Oluwaseun Akinola',
  role: 'Medical Director, Family Medicine',
  bio: 'Board-certified in Family Medicine, Dr. Akinola leads our clinical team with a commitment to comprehensive, patient-centered care for all ages. He specializes in chronic disease management, preventive care, and health education.',
  image: 'https://images.unsplash.com/photo-1612349317150-e413f6a5b16d?w=400&q=80'
}, {
  name: 'Dr. Amara Osei',
  role: 'Internal Medicine',
  bio: 'Dr. Osei brings over 15 years of experience in internal medicine, with special interests in diabetes management, hypertension, and cardiovascular health. She is passionate about preventive care and patient education.',
  image: 'https://images.unsplash.com/photo-1559839734-2b71ea197ec2?w=400&q=80'
}, {
  name: 'Dr. Maria Rodriguez',
  role: 'Pediatrics',
  bio: 'A dedicated pediatrician, Dr. Rodriguez provides well-child visits, immunizations, developmental screenings, and acute care for children from birth through adolescence.',
  image: 'https://images.unsplash.com/photo-1594824476967-48c8b964ac31?w=400&q=80'
}, {
  name: 'Dr. James Mensah',
  role: 'Urgent Care Physician',
  bio: 'Dr. Mensah specializes in urgent care medicine, treating walk-in patients for injuries, infections, and acute illnesses. He ensures patients receive timely, high-quality care when they need it most.',
  image: 'https://images.unsplash.com/photo-1537368910025-700350fe46c7?w=400&q=80'
}, {
  name: 'Patricia Williams, FNP-BC',
  role: 'Family Nurse Practitioner',
  bio: "Patricia is a board-certified Family Nurse Practitioner who provides primary care services including annual physicals, chronic disease management, and women's health screenings.",
  image: 'https://images.unsplash.com/photo-1551836022-d5d88e9218df?w=400&q=80'
}, {
  name: 'David Chen, PA-C',
  role: 'Physician Assistant',
  bio: 'David supports our medical team across primary and urgent care, with expertise in minor procedures, lab interpretation, and patient follow-up. He is bilingual in English and Mandarin.',
  image: 'https://images.unsplash.com/photo-1651008376811-b90baee60c1f?w=400&q=80'
}];
const values = [{
  icon: <HeartHandshakeIcon className="h-8 w-8" data-id="element-768" />,
  title: 'Patient-Centered',
  description: "Every decision starts with what's best for the patient. We listen, we care, and we act with empathy."
}, {
  icon: <UsersIcon className="h-8 w-8" data-id="element-769" />,
  title: 'Community Focused',
  description: 'We are rooted in the Landover and Oxon Hill communities. Our team lives and invests where we serve.'
}, {
  icon: <AwardIcon className="h-8 w-8" data-id="element-770" />,
  title: 'PCMH Recognized',
  description: 'As a Patient-Centered Medical Home, we deliver coordinated, evidence-based care that exceeds national benchmarks.'
}, {
  icon: <ShieldCheckIcon className="h-8 w-8" data-id="element-771" />,
  title: 'Quality & Safety',
  description: 'We maintain the highest standards of clinical quality, patient safety, and HIPAA compliance in everything we do.'
}];
const containerVariants = {
  hidden: {
    opacity: 0
  },
  show: {
    opacity: 1,
    transition: {
      staggerChildren: 0.1
    }
  }
};
const itemVariants = {
  hidden: {
    opacity: 0,
    y: 20
  },
  show: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.5,
      ease: 'easeOut'
    }
  }
};
export function TeamPage() {
  return <>
      <PageHero eyebrow="Our Team" title="Meet the Providers Who Care for You" subtitle="Our multi-disciplinary team of physicians, nurse practitioners, and support staff are united by a shared mission: delivering outstanding healthcare to every patient." bgImage="https://images.unsplash.com/photo-1579684385127-1ef15d508118?w=1920&q=80" data-id="element-772" />

      <section className="py-20 md:py-28 bg-white" aria-labelledby="providers-heading" data-id="element-773">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-774">
          <div className="text-center mb-14" data-id="element-775">
            <h2 id="providers-heading" className="text-3xl md:text-4xl font-bold text-navy-500 mb-4" data-id="element-776">
              Our Providers
            </h2>
            <p className="text-lg text-gray-500 max-w-2xl mx-auto" data-id="element-777">
              Experienced, compassionate professionals dedicated to your health
              and well-being.
            </p>
          </div>
          <motion.div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8" variants={containerVariants} initial="hidden" whileInView="show" viewport={{
          once: true,
          margin: '-80px'
        }} data-id="element-778">
            {providers.map(person => <motion.div key={person.name} variants={itemVariants} className="bg-white border border-[#E5E9F0] rounded-[10px] overflow-hidden" data-id="element-779">
                <div className="h-64 overflow-hidden" data-id="element-780">
                  <img src={person.image} alt={person.name} className="w-full h-full object-cover object-top" data-id="element-781" />
                </div>
                <div className="p-6" data-id="element-782">
                  <h3 className="text-lg font-bold text-navy-500" data-id="element-783">
                    {person.name}
                  </h3>
                  <p className="text-sm font-semibold text-teal-500 mb-3" data-id="element-784">
                    {person.role}
                  </p>
                  <p className="text-sm text-gray-500 leading-relaxed" data-id="element-785">
                    {person.bio}
                  </p>
                </div>
              </motion.div>)}
          </motion.div>
        </div>
      </section>

      <section className="py-20 md:py-28 bg-gray-50" aria-labelledby="values-heading" data-id="element-786">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-787">
          <div className="text-center mb-14" data-id="element-788">
            <h2 id="values-heading" className="text-3xl md:text-4xl font-bold text-navy-500 mb-4" data-id="element-789">
              Our Values
            </h2>
            <p className="text-lg text-gray-500 max-w-2xl mx-auto" data-id="element-790">
              The principles that guide everything we do at Healthy Choice
              Family Clinic.
            </p>
          </div>
          <motion.div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6" variants={containerVariants} initial="hidden" whileInView="show" viewport={{
          once: true,
          margin: '-80px'
        }} data-id="element-791">
            {values.map(v => <motion.div key={v.title} variants={itemVariants} className="bg-white border border-[#E5E9F0] rounded-[10px] p-8 text-center" data-id="element-792">
                <div className="inline-flex items-center justify-center h-14 w-14 rounded-[10px] bg-navy-50 text-navy-500 mb-5" data-id="element-793">
                  {v.icon}
                </div>
                <h3 className="text-base font-bold text-navy-500 mb-2" data-id="element-794">
                  {v.title}
                </h3>
                <p className="text-sm text-gray-500 leading-relaxed" data-id="element-795">
                  {v.description}
                </p>
              </motion.div>)}
          </motion.div>
        </div>
      </section>
    </>;
}